import React, { useState } from "react";
import { useAppSelector } from "../../../store/hooks";
import { ComputerSelector } from "../components/core/ComputerSelector";
import { SoftwareSelector } from "../components/core/SoftwareSelector";
import { BreakawaySoftwareSelector } from "../components/core/BreakawaySoftwareSelector";
import { selectIsBreakaway } from "../../../store/selectors/breakawayHullSelectors";

/**
 * Step 6: Install Computer & Software
 * Allows users to select and configure the ship's computer systems and software
 * according to Traveller rules and requirements
 *
 * @component
 * @example
 * ```tsx
 * <ComputerStep />
 * ```
 */
export const ComputerStep: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"computer" | "software">(
    "computer",
  );
  
  // Check if breakaway hulls are configured
  const isBreakaway = useAppSelector(selectIsBreakaway);
  const breakawayHull = useAppSelector((state) => state.breakawayHull);
  const hasBreakawayHulls = isBreakaway || breakawayHull?.isBreakaway;

  const tabs = [
    { id: "computer", label: "Computer Systems", icon: "💻" },
    { id: "software", label: "Software Packages", icon: "📦" },
  ] as const;

  return (
    <div className="space-y-6 pb-32">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Computer & Software Configuration
        </h2>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-600 mb-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                    : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500"
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === "computer" && (
            <div className="computer-tab">
              <ComputerSelector />
            </div>
          )}

          {activeTab === "software" && (
            <div className="software-tab">
              {hasBreakawayHulls ? (
                <BreakawaySoftwareSelector />
              ) : (
                <SoftwareSelector />
              )}
            </div>
          )}
        </div>
        {/* Removed Integration Note section */}
      </div>
    </div>
  );
};
